import axios from '@api';

import type {
  CustomGroup,
  CustomGroupAttributeElement,
  CustomGroupBaseResponse,
  CustomGroupCreateResponse,
  CustomGroupHierarchy,
  CustomGroupHierarchySearchResponse,
  CustomGroupId,
  CustomGroupPage,
  CustomGroupPageResponse,
  CustomGroupResponse,
  CustomGroupType,
} from './types';
import {
  getCustomGroupType,
  transformCustomGroupData,
  transformCustomGroupsData,
} from './utilities/helpers';

interface CreateCustomGroupOptions {
  definition?: Record<string, string>;
  name: string;
  type: CustomGroupType;
}

interface ReadCustomGroupsOptions {
  order?: 'asc' | 'desc';
  page: number;
  query?: string;
  signal?: AbortSignal;
  size: number;
  sortBy?: string;
  tags?: string[];
  type: CustomGroupType;
}

interface ShareCustomGroupWithClientOptions {
  clientId: number;
  ids: CustomGroupId[];
}

interface ShareCustomGroupWithUsersOptions {
  ids: CustomGroupId[];
  skipEmail?: boolean;
  userIds: number[];
}

export const createCustomGroup = ({ definition = {}, name, type }: CreateCustomGroupOptions) => {
  return axios<CustomGroupCreateResponse>({
    data: {
      definition,
      name,
    },
    method: 'post',
    url: `/v2/api/customgroups?group_type=${type}`,
    withCredentials: true,
  });
};

export const deleteCustomGroups = ({ ids }: { ids: CustomGroupId[] }) => {
  return axios<void>({
    data: ids,
    method: 'delete',
    url: '/v2/api/customgroups',
    withCredentials: true,
  });
};

export const readCustomGroup = ({ id, signal }: { id: CustomGroupId; signal?: AbortSignal }) => {
  return axios<CustomGroup>({
    method: 'get',
    signal,
    transformResponse: [
      (data: string) => transformCustomGroupData(JSON.parse(data) as CustomGroupResponse),
    ],
    url: `/v2/api/customgroups/${id}`,
    withCredentials: true,
  });
};

export const readCustomGroups = ({
  order,
  page,
  query,
  signal,
  size,
  sortBy,
  tags,
  type,
}: ReadCustomGroupsOptions) => {
  return axios<CustomGroupPage>({
    method: 'get',
    params: {
      order_by: sortBy ? order : undefined,
      page,
      search_bar_query: query?.length ? query : undefined,
      size,
      sort_by: sortBy,
      tags: tags?.length ? tags.join(',') : undefined,
    },
    signal,
    transformResponse: [
      (data: string) => transformCustomGroupsData(JSON.parse(data) as CustomGroupPageResponse),
    ],
    url: `/v2/api/customgroups/list/${type}`,
    withCredentials: true,
  });
};

export const readCustomGroupAttributeElements = ({
  attributeId,
  search,
  signal,
  type,
}: {
  attributeId: string;
  search: string;
  signal?: AbortSignal;
  type: CustomGroupType;
}) => {
  const groupType = getCustomGroupType(type);
  return axios<CustomGroupAttributeElement[]>({
    method: 'get',
    params: {
      attributeId,
      search,
    },
    signal,
    url: `/api/prompts/${groupType}/search`,
    withCredentials: true,
  });
};

export const readCustomGroupHierarchy = ({
  signal,
  type,
}: {
  signal?: AbortSignal;
  type: CustomGroupType;
}) => {
  return axios<CustomGroupHierarchy>({
    method: 'get',
    signal,
    url: `/api/hierarchies/${type}`,
    withCredentials: true,
  });
};

export const searchCustomGroupHierarchy = ({
  search,
  signal,
  type,
}: {
  search: string;
  signal?: AbortSignal;
  type: CustomGroupType;
}) => {
  return axios<CustomGroupHierarchySearchResponse>({
    method: 'get',
    signal,
    url: `/api/hierarchies/${type}/search?expression=${encodeURIComponent(search)}`,
    withCredentials: true,
  });
};

export const shareCustomGroupWithClient = ({
  clientId,
  ids,
}: ShareCustomGroupWithClientOptions) => {
  return axios<string>({
    data: {
      customgroup_ids: ids,
      recipient_client_id: clientId,
    },
    method: 'post',
    url: '/v2/api/customgroups/client_share',
    withCredentials: true,
  });
};

export const shareCustomGroupWithUsers = ({
  ids,
  skipEmail = false,
  userIds,
}: ShareCustomGroupWithUsersOptions) => {
  return axios<string>({
    data: {
      customgroup_ids: ids,
      recipient_ids: userIds,
      skip_email: skipEmail,
    },
    method: 'post',
    url: '/v2/api/customgroups/user_share',
    withCredentials: true,
  });
};

export const updateCustomGroupBreakouts = ({
  definition,
  id,
}: {
  definition: Record<string, string>;
  id: CustomGroupId;
}) => {
  return axios<CustomGroupBaseResponse>({
    data: {
      definition,
    },
    method: 'put',
    url: `/v2/api/customgroups/${id}`,
    withCredentials: true,
  });
};

export const updateCustomGroupName = ({ id, name }: { id: CustomGroupId; name: string }) => {
  return axios<CustomGroupBaseResponse>({
    data: {
      name,
    },
    method: 'put',
    url: `/v2/api/customgroups/${id}`,
    withCredentials: true,
  });
};
